import { useState } from "react";
import { observer } from "mobx-react-lite";
import contentManager from "../../../stores/content-manager"; // for logic step
import { IColumnsBlockData } from "../../../types/content-block-data";
import SatoriCollapsiblePanel from "../shared/satori-ui/collapsible-panel";
import SatoriDimensionsBox from "../shared/satori-ui/dimensions-box"; // for logic step
import { Columns3Cog, Link, PanelLeftDashed, Space, Unlink } from "lucide-react";
import { COLUMN_CONFIGS } from "../../../types/constants";

/**
 * ColumnsOptions – Sidebar Editing Panel for Columns Blocks
 *
 * Provides an interactive sidebar panel for configuring the layout, spacing, and cell styling
 * of a Columns block in the Satori PageBuilder. Supports selection of column layouts, column gap,
 * cell padding/margin, and border radius, all with linked and individual side controls.
 *
 * @component
 * @category OptionsPanel
 *
 * @param {Object} props - Component props.
 * @param {string} props.blockId - The unique ID of the Columns content block to edit.
 *
 * @example
 * // Render options panel for a given Columns block
 * <ColumnsOptions blockId={selectedBlockId} />
 *
 * @remarks
 * - Fetches and updates the target Columns block's data from the MobX contentManager store.
 * - Allows interactive layout selection (e.g., 1–4 columns, varied widths) and updates column configuration.
 * - All changes call `contentManager.setColumnsConfig` for atomic and reactive state updates.
 * - Cell padding, margin, and radius controls support both linked (all sides) and individual settings, with full px/rem unit selection.
 * - Handles user constraints when reducing columns—prevents data loss by requiring empty columns before removal.
 * - Designed to be extended in future for per-cell configuration and additional style options.
 * - Wrapped in MobX's `observer()` to enable real-time UI reactivity.
 *
 * @see contentManager.setColumnsConfig
 * @see ButtonOptions – For a parallel options panel pattern
 */
export default observer(function ColumnsOptions({ blockId }: { blockId: string }) {
  // Lookup the current block (assume blockData is IColumnsBlockData)
  const block = contentManager.getContentBlockById(blockId);
  if (!block || block.blockType !== "columns") return null;
  
  const columnsData = block.blockData as IColumnsBlockData | undefined;
  const columnWidths = columnsData?.columnWidths ?? [];
  const colCount = columnWidths.length || columnsData?.children?.length || 0;

  // Helper: does this config match current config?
  function isSelected(configWidths: number[]) {
    if (configWidths.length !== colCount) return false;
    return configWidths.every((w, idx) => Math.abs(w - columnWidths[idx]) < 0.01);
  }

  // Handle click to change layout
  function handleSelectConfig(widths: number[]) {
    // Defensive: Make sure columnsData exists before using it
    if (!columnsData) return; // Or log an error, or show a fallback message

    if (
      widths.length === colCount &&
      widths.every((w, idx) => Math.abs(w - columnWidths[idx]) < 0.01)
    ) {
      return;
    }

    // If adding columns, add empty arrays for new columns:
    if (widths.length > columnsData.children.length) {
      const newChildren = [
        ...columnsData.children,
        ...Array(widths.length - columnsData.children.length).fill([]),
      ];
      contentManager.setColumnsConfig(blockId, { children: newChildren, columnWidths: widths });
    } else if (widths.length < columnsData.children.length) {
      const currentCols = columnsData.children;
      const newCols = widths.length;
      const toRemove = currentCols.length - newCols;

      // Find all indices of empty columns
      const emptyColIndices = currentCols
        .map((col, idx) => (col.length === 0 ? idx : -1))
        .filter(idx => idx !== -1);

      if (emptyColIndices.length >= toRemove) {
        // Remove any 'toRemove' number of empty columns (prefer removing from end, but not required)
        let newChildren = [...currentCols];
        // Remove empty columns by their indices (from the end, so indices don't shift)
        emptyColIndices.slice(-toRemove).reverse().forEach(idx => {
          newChildren.splice(idx, 1);
        });
        // Only keep as many columns as needed
        newChildren = newChildren.slice(0, newCols);
        contentManager.setColumnsConfig(blockId, { children: newChildren, columnWidths: widths });
      } else {
        alert(
          "To change the layout, first move any content blocks from the column(s) you plan to remove."
        );
        return;
      }
    } else {
      // Same number of columns, just update widths
      contentManager.setColumnsConfig(blockId, { children: columnsData.children, columnWidths: widths });
    }
  }

  return (
    <section className="flex flex-col">
      {/* Layout Picker */}
      <SatoriCollapsiblePanel
        icon={<Columns3Cog size={18} />}
        title={"Layout Configurations"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-2">
          {COLUMN_CONFIGS.map((config, idx) => (
            <div
              key={config.tooltip}
              className={`
                flex items-center border rounded cursor-pointer transition
                group
                ${isSelected(config.widths)
                  ? "border-blue-500 bg-blue-50 shadow"
                  : "hover:border-blue-500"}
              `}
              style={{ minHeight: 40, padding: 2 }}
              title={config.tooltip}
              onClick={() => handleSelectConfig(config.widths)}
            >
              <div className="flex flex-row w-full h-8 items-center">
                {config.widths.map((w, i) => (
                  <div
                    key={i}
                    style={{ width: `${w * 100}%`, height: 32 }}
                    className="bg-gray-300 mx-1 rounded transition group-hover:bg-blue-200"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </SatoriCollapsiblePanel>

      {/* Gaps Section */}
      <SatoriCollapsiblePanel
        icon={<Space size={18} />}
        title={"Columns Spacing"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-2">
          {/* Row: Label left, Input right, Unit immediately after */}
          <SatoriDimensionsBox
            label="Column Gap"
            value={columnsData?.columnGap?.value || ""}
            unit={columnsData?.columnGap?.unit || "px"}
            onValueChange={(val) =>
              contentManager.setColumnsConfig(blockId, {
                ...columnsData,
                columnGap: {
                  value: val,
                  unit: columnsData?.columnGap?.unit || "px",
                },
                children: columnsData?.children,
                columnWidths: columnsData?.columnWidths,
              })
            }
            onUnitChange={(unit) =>
              contentManager.setColumnsConfig(blockId, {
                ...columnsData,
                columnGap: { value: columnsData?.columnGap?.value || "", unit },
                children: columnsData?.children,
                columnWidths: columnsData?.columnWidths,
              })
            }
          />
          {/* Decided to hide this for version 1.0 because row-gap is only useful if there are multiple rows */}
          {/*<SatoriDimensionsBox
            label="Row Gap"
            value={columnsData?.rowGap?.value || ""}
            unit={columnsData?.rowGap?.unit || "px"}
            onValueChange={(val) =>
              contentManager.setColumnsConfig(blockId, {
                ...columnsData,
                rowGap: { value: val, unit: columnsData?.rowGap?.unit || "px" },
                children: columnsData?.children,
                columnWidths: columnsData?.columnWidths,
              })
            }
            onUnitChange={(unit) =>
              contentManager.setColumnsConfig(blockId, {
                ...columnsData,
                rowGap: { value: columnsData?.rowGap?.value || "", unit },
                children: columnsData?.children,
                columnWidths: columnsData?.columnWidths,
              })
            }
          />*/}
        </div>
      </SatoriCollapsiblePanel>

      {/* Cell Spacing Section (i.e. margins, paddings, radius etc) */}
      <SatoriCollapsiblePanel
        icon={<PanelLeftDashed size={18} />}
        title={"Cell Spacing"}
        defaultOpen={true}
      >
        <div className="flex items-center gap-2 mb-2 w-full justify-between">
          <span className="text-sm">Cell Padding</span>
          <button
            onClick={() =>
              contentManager.setColumnsConfig(blockId, {
                cellPadding: {
                  ...columnsData?.cellPadding!,
                  linked: !columnsData?.cellPadding?.linked,
                },
              })
            }
            className="ml-1 p-1"
            title={
              columnsData?.cellPadding?.linked
                ? "Unlink sides"
                : "Link all sides"
            }
          >
            {columnsData?.cellPadding?.linked ? (
              <Link size={16} />
            ) : (
              <Unlink size={16} />
            )}
          </button>
        </div>
        {columnsData?.cellPadding?.linked ? (
          <div className="flex flex-col gap-2 mb-2">
            <SatoriDimensionsBox
              value={columnsData?.cellPadding?.left || ""}
              unit={columnsData?.cellPadding?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    top: val,
                    right: val,
                    bottom: val,
                    left: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2 mb-2 w-full">
            <SatoriDimensionsBox
              label={"Top"}
              value={columnsData?.cellPadding?.top || ""}
              unit={columnsData?.cellPadding?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    top: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
            <SatoriDimensionsBox
              label={"Right"}
              value={columnsData?.cellPadding?.right || ""}
              unit={columnsData?.cellPadding?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    right: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
            <SatoriDimensionsBox
              label={"Bottom"}
              value={columnsData?.cellPadding?.bottom || ""}
              unit={columnsData?.cellPadding?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    bottom: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
            <SatoriDimensionsBox
              label={"Left"}
              value={columnsData?.cellPadding?.left || ""}
              unit={columnsData?.cellPadding?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    left: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellPadding: {
                    ...columnsData?.cellPadding!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
          </div>
        )}

        {/* Cell Margins sub-secton */}
        <div className="flex items-center gap-2 my-2 w-full justify-between">
          <span className="text-sm">Cell Margins</span>
          <button
            onClick={() =>
              contentManager.setColumnsConfig(blockId, {
                cellMargin: {
                  ...columnsData?.cellMargin!,
                  linked: !columnsData?.cellMargin?.linked,
                },
              })
            }
            className="ml-1 p-1"
            title={
              columnsData?.cellMargin?.linked
                ? "Unlink sides"
                : "Link all sides"
            }
          >
            {columnsData?.cellMargin?.linked ? (
              <Link size={16} />
            ) : (
              <Unlink size={16} />
            )}
          </button>
        </div>
        {columnsData?.cellMargin?.linked ? (
          <div className="flex flex-col gap-2">
            <SatoriDimensionsBox
              value={columnsData?.cellMargin?.left || ""}
              unit={columnsData?.cellMargin?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    top: val,
                    right: val,
                    bottom: val,
                    left: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2 w-full">
            <SatoriDimensionsBox
              label={"Top"}
              value={columnsData?.cellMargin?.top || ""}
              unit={columnsData?.cellMargin?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    top: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
            <SatoriDimensionsBox
              label={"Right"}
              value={columnsData?.cellMargin?.right || ""}
              unit={columnsData?.cellMargin?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    right: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
            <SatoriDimensionsBox
              label={"Bottom"}
              value={columnsData?.cellMargin?.bottom || ""}
              unit={columnsData?.cellMargin?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    bottom: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
            <SatoriDimensionsBox
              label={"Left"}
              value={columnsData?.cellMargin?.left || ""}
              unit={columnsData?.cellMargin?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    left: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellMargin: {
                    ...columnsData?.cellMargin!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
          </div>
        )}

        {/* Cell Margins sub-secton */}
        <div className="flex items-center gap-2 my-2 w-full justify-between">
          <span className="text-sm">Cell Radius</span>
          <button
            onClick={() =>
              contentManager.setColumnsConfig(blockId, {
                cellRadius: {
                  ...columnsData?.cellRadius!,
                  linked: !columnsData?.cellRadius?.linked,
                },
              })
            }
            className="ml-1 p-1"
            title={
              columnsData?.cellRadius?.linked
                ? "Unlink sides"
                : "Link all sides"
            }
          >
            {columnsData?.cellRadius?.linked ? (
              <Link size={16} />
            ) : (
              <Unlink size={16} />
            )}
          </button>
        </div>
        {columnsData?.cellRadius?.linked ? (
          <div className="flex flex-col gap-2">
            <SatoriDimensionsBox
              value={columnsData?.cellRadius?.topLeft || ""}
              unit={columnsData?.cellRadius?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    topLeft: val,
                    topRight: val,
                    bottomRight: val,
                    bottomLeft: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2 w-full">
            <SatoriDimensionsBox
              label={"Top Left"}
              value={columnsData?.cellRadius?.topLeft || ""}
              unit={columnsData?.cellRadius?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    topLeft: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
            <SatoriDimensionsBox
              label={"Top Right"}
              value={columnsData?.cellRadius?.topRight || ""}
              unit={columnsData?.cellRadius?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    topRight: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
            <SatoriDimensionsBox
              label={"Bottom Right"}
              value={columnsData?.cellRadius?.bottomRight || ""}
              unit={columnsData?.cellRadius?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    bottomRight: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
            <SatoriDimensionsBox
              label={"Bottom Left"}
              value={columnsData?.cellRadius?.bottomLeft || ""}
              unit={columnsData?.cellRadius?.unit || "px"}
              onValueChange={(val) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    bottomLeft: val,
                  },
                })
              }
              onUnitChange={(unitVal) =>
                contentManager.setColumnsConfig(blockId, {
                  cellRadius: {
                    ...columnsData?.cellRadius!,
                    unit: unitVal || "px",
                  },
                })
              }
            />
          </div>
        )}
      </SatoriCollapsiblePanel>
    </section>
  );
});
